import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CTAButton, ProductImage, CheckItem } from './ui';
import { Shield, Brain, Users, Heart, Target, Clock, CheckCircle, Sparkles, AlertCircle } from 'lucide-react';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export const HeroSection = () => <motion.section initial="hidden" animate="visible" variants={fadeInUp} className="pt-16 pb-12 text-center" data-id="element-76">
    <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-[#1B3A5C] leading-tight mb-4 tracking-tight" data-id="element-77">
      Tu hijo no se está desarrollando como debería{' '}
      <br className="hidden md:block" data-id="element-78" />
      <span className="text-[#F47B20]" data-id="element-79">
        ¡Estás perdiendo una oportunidad clave!!
      </span>
    </h1>
    <p className="text-xl md:text-2xl text-gray-600 font-semibold mb-10" data-id="element-80">
      Rutinas y estimulación de motricidad infantil
    </p>
    <div className="mb-12" data-id="element-81">
      <ProductImage className="max-w-[340px] max-h-[200px] object-contain" data-id="element-82" />
    </div>
    <CTAButton className="mb-8" data-id="element-83">SÍ, QUIERO AYUDAR A MI HIJO</CTAButton>
  </motion.section>;
export const ProblemSection = () => <motion.section initial="hidden" whileInView="visible" viewport={{
  once: true,
  margin: '-100px'
}} variants={fadeInUp} className="py-16 my-8" data-id="element-84">
    <div className="bg-[#1B3A5C] rounded-3xl p-8 md:p-12 text-center shadow-2xl relative overflow-hidden" data-id="element-85">
      <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white to-transparent" data-id="element-86"></div>
      <AlertCircle className="w-12 h-12 text-[#F47B20] mx-auto mb-6 opacity-80" data-id="element-87" />
      <p className="text-2xl md:text-3xl font-medium text-white leading-relaxed mb-6" data-id="element-88">
        "¿Mi hijo debería estar haciendo eso ya?"
      </p>
      <p className="text-xl text-blue-200 font-light" data-id="element-89">
        No estás solo. Y no es tu culpa. <br className="hidden md:block" data-id="element-90" />
        <span className="font-bold text-white" data-id="element-91">
          El problema no es falta de amor, es falta de dirección.
        </span>
      </p>
    </div>
  </motion.section>;
export const StakesSection = () => <motion.section initial="hidden" whileInView="visible" viewport={{
  once: true,
  margin: '-100px'
}} variants={staggerContainer} className="py-16" data-id="element-92">
    <motion.div variants={fadeInUp} className="text-center mb-12" data-id="element-93">
      <h2 className="text-3xl md:text-4xl font-black text-[#1B3A5C] mb-4" data-id="element-94">
        La motricidad es la base de <span className="text-[#F47B20]" data-id="element-95">TODO</span>
      </h2>
      <p className="text-xl text-gray-600" data-id="element-96">
        Sin cimientos sólidos, todo lo demás cuesta el doble.
      </p>
    </motion.div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-97">
      {[{
      icon: Shield,
      title: 'Confianza',
      desc: 'Seguridad en cada paso',
      color: 'bg-blue-50 text-blue-600'
    }, {
      icon: Brain,
      title: 'Aprendizaje',
      desc: 'Conexiones neuronales más rápidas',
      color: 'bg-purple-50 text-purple-600'
    }, {
      icon: Users,
      title: 'Desarrollo Social',
      desc: 'Juego e interacción sin miedo',
      color: 'bg-green-50 text-green-600'
    }, {
      icon: Heart,
      title: 'Autoestima',
      desc: 'Creer en sí mismos',
      color: 'bg-red-50 text-red-600'
    }].map((item, i) => <motion.div key={i} variants={fadeInUp} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-5 hover:shadow-md transition-shadow" data-id="element-98">
          <div className={`p-4 rounded-xl ${item.color}`} data-id="element-99">
            <item.icon className="w-8 h-8" data-id="element-100" />
          </div>
          <div data-id="element-101">
            <h3 className="text-xl font-bold text-[#1B3A5C]" data-id="element-102">{item.title}</h3>
            <p className="text-gray-500" data-id="element-103">{item.desc}</p>
          </div>
        </motion.div>)}
    </div>
  </motion.section>;
export const MythsSection = () => <motion.section initial="hidden" whileInView="visible" viewport={{
  once: true,
  margin: '-100px'
}} variants={fadeInUp} className="py-16" data-id="element-104">
    <div className="bg-orange-50 rounded-3xl p-8 md:p-12 border border-orange-100 text-center" data-id="element-105">
      <span className="text-sm font-bold text-[#F47B20] tracking-widest uppercase mb-4 block" data-id="element-106">
        El Gran Mito
      </span>
      <p className="text-2xl md:text-3xl font-medium text-gray-800 italic mb-8" data-id="element-107">
        "Ya lo aprenderá solo. Cada niño tiene su ritmo."
      </p>
      <div className="w-16 h-1 bg-[#F47B20] mx-auto mb-8 rounded-full" data-id="element-108"></div>
      <p className="text-2xl font-black text-[#1B3A5C]" data-id="element-109">
        El desarrollo motriz no ocurre por casualidad.
        <br data-id="element-110" />
        <span className="text-[#F47B20]" data-id="element-111">Se estimula y se construye.</span>
      </p>
    </div>
  </motion.section>;
export const VisionSection = () => <motion.section initial="hidden" whileInView="visible" viewport={{
  once: true,
  margin: '-100px'
}} variants={staggerContainer} className="py-16" data-id="element-112">
    <motion.div variants={fadeInUp} className="text-center mb-12" data-id="element-113">
      <Sparkles className="w-10 h-10 text-[#F47B20] mx-auto mb-4" data-id="element-114" />
      <h2 className="text-3xl md:text-4xl font-black text-[#1B3A5C]" data-id="element-115">
        Imagina esto por un momento…
      </h2>
    </motion.div>

    <div className="grid md:grid-cols-2 gap-8" data-id="element-116">
      <motion.div variants={fadeInUp} className="bg-white p-8 rounded-3xl shadow-lg border-t-8 border-[#F47B20]" data-id="element-117">
        <h3 className="text-2xl font-black text-[#1B3A5C] mb-6 flex items-center gap-3" data-id="element-118">
          🧒 Tu hijo…
        </h3>
        <ul className="space-y-4 text-lg text-gray-700 font-medium" data-id="element-119">
          <li className="flex items-center gap-3" data-id="element-120">
            <CheckCircle className="w-6 h-6 text-green-500" data-id="element-121" /> Se mueve con
            seguridad
          </li>
          <li className="flex items-center gap-3" data-id="element-122">
            <CheckCircle className="w-6 h-6 text-green-500" data-id="element-123" /> Juega con
            confianza
          </li>
          <li className="flex items-center gap-3" data-id="element-124">
            <CheckCircle className="w-6 h-6 text-green-500" data-id="element-125" /> Aprende más
            rápido
          </li>
        </ul>
      </motion.div>

      <motion.div variants={fadeInUp} className="bg-white p-8 rounded-3xl shadow-lg border-t-8 border-[#1B3A5C]" data-id="element-126">
        <h3 className="text-2xl font-black text-[#1B3A5C] mb-6 flex items-center gap-3" data-id="element-127">
          🫂 Y tú…
        </h3>
        <ul className="space-y-4 text-lg text-gray-700 font-medium" data-id="element-128">
          <li className="flex items-center gap-3" data-id="element-129">
            <CheckCircle className="w-6 h-6 text-[#1B3A5C]" data-id="element-130" /> Sabes exactamente
            qué hacer
          </li>
          <li className="flex items-center gap-3" data-id="element-131">
            <CheckCircle className="w-6 h-6 text-[#1B3A5C]" data-id="element-132" /> Te sientes
            tranquilo
          </li>
          <li className="flex items-center gap-3" data-id="element-133">
            <CheckCircle className="w-6 h-6 text-[#1B3A5C]" data-id="element-134" /> Disfrutas verlo
            avanzar
          </li>
        </ul>
      </motion.div>
    </div>
  </motion.section>;
export const MethodSection = () => <motion.section initial="hidden" whileInView="visible" viewport={{
  once: true,
  margin: '-100px'
}} variants={staggerContainer} className="py-16 text-center" data-id="element-135">
    <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-black text-[#1B3A5C] mb-12" data-id="element-136">
      Lo que necesitas es un{' '}
      <span className="text-[#F47B20]" data-id="element-137">sistema claro.</span>
    </motion.h2>

    <div className="flex flex-wrap justify-center gap-4 md:gap-8" data-id="element-138">
      {[{
      icon: Target,
      label: 'Qué hacer'
    }, {
      icon: Sparkles,
      label: 'Cómo hacerlo'
    }, {
      icon: Clock,
      label: 'Cuándo hacerlo'
    }, {
      icon: Brain,
      label: 'Por qué funciona'
    }].map((item, i) => <motion.div key={i} variants={fadeInUp} className="flex flex-col items-center gap-3 bg-white px-6 py-4 rounded-2xl shadow-sm border border-gray-100 min-w-[140px]" data-id="element-139">
          <item.icon className="w-8 h-8 text-[#F47B20]" data-id="element-140" />
          <span className="font-bold text-[#1B3A5C]" data-id="element-141">{item.label}</span>
        </motion.div>)}
    </div>
  </motion.section>;
export const ProductReveal = () => {};
export const ComparisonSection = () => <motion.section initial="hidden" whileInView="visible" viewport={{
  once: true,
  margin: '-100px'
}} variants={fadeInUp} className="py-16" data-id="element-142">
    <div className="bg-white p-8 md:p-10 rounded-3xl shadow-xl border border-gray-100 max-w-2xl mx-auto" data-id="element-143">
      <h3 className="text-3xl font-black text-[#1B3A5C] mb-8 text-center" data-id="element-144">
        Dentro descubrirás:
      </h3>
      <div className="space-y-2" data-id="element-145">
        <CheckItem data-id="element-146">Rutinas organizadas por edad</CheckItem>
        <CheckItem data-id="element-147">Ejercicios simples paso a paso</CheckItem>
        <CheckItem data-id="element-148">Actividades para hacer en casa</CheckItem>
        <CheckItem data-id="element-149">Métodos respaldados científicamente</CheckItem>
      </div>
      <p className="text-xl font-bold text-center text-[#F47B20] mt-8" data-id="element-150">
        Sin confusión. Sin sobrecarga.
      </p>
    </div>
  </motion.section>;
export const ResultsSection = () => <motion.section initial="hidden" whileInView="visible" viewport={{
  once: true,
  margin: '-100px'
}} variants={staggerContainer} className="py-16 text-center" data-id="element-151">
    <motion.h3 variants={fadeInUp} className="text-3xl font-black text-[#1B3A5C] mb-10" data-id="element-152">
      Resultados que notarás rápidamente:
    </motion.h3>

    <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-3 mb-12" data-id="element-153">
      {['🎯 Mejor coordinación', '⚖️ Mayor equilibrio', '🛡️ Más seguridad', '⭐ Más confianza'].map((result, i) => <span key={i} className="bg-green-50 text-green-700 border border-green-200 px-6 py-3 rounded-full font-bold text-lg shadow-sm" data-id="element-154">
          {result}
        </span>)}
    </motion.div>
  </motion.section>;
export const StorySection = () => <motion.section initial="hidden" whileInView="visible" viewport={{
  once: true,
  margin: '-100px'
}} variants={fadeInUp} className="py-20 text-center" data-id="element-155">
    <h3 className="text-4xl md:text-5xl font-black text-[#1B3A5C] mb-6 leading-tight" data-id="element-156">
      Tu hijo tiene un potencial enorme.
    </h3>
    <p className="text-2xl font-medium text-gray-600 mb-8" data-id="element-157">
      Pero el potencial sin guía…{' '}
      <span className="text-[#F47B20] font-bold" data-id="element-158">se queda dormido.</span>
    </p>
    <p className="text-xl font-bold text-[#1B3A5C] bg-orange-50 inline-block px-8 py-4 rounded-2xl" data-id="element-159">
      Aún estás a tiempo de marcar la diferencia.
    </p>
  </motion.section>;